import { Timeline, formatComparedToTimeline } from '@/utils/date'
import { RadioOptionPicker } from '../RadioOptionPicker/RadioOptionPicker'
import { Option } from '../types'
import { useTranslation } from 'react-i18next'
import { ComparisonPeriod } from '@/hooks'
import { useCallback } from 'react'

export const ComparedToSelect = (props: {
    value: ComparisonPeriod
    previousYearTimeline?: Timeline
    baselineYearTimeline?: Timeline
    onChange: (type: ComparisonPeriod) => void
}) => {
    const { t } = useTranslation('translations')
    const { value, previousYearTimeline, baselineYearTimeline, onChange } =
        props

    const getWarningMessage = (timeline: Timeline | undefined, key: string) =>
        timeline ? undefined : t(key)

    const options: Array<Option<ComparisonPeriod>> = [
        {
            disabled: !previousYearTimeline,
            value: 'previousYear',
            label: t('analytics.benchmarking.comparedTo.previousYear.label'),
            subtitle: t(
                'analytics.benchmarking.comparedTo.previousYear.subtitle'
            ),
            warningMessage: getWarningMessage(
                previousYearTimeline,
                'analytics.benchmarking.comparedTo.previousYear.no_previous_year'
            ),
        },
        {
            disabled: !baselineYearTimeline,
            value: 'baselineYear',
            label: t('analytics.benchmarking.comparedTo.baselineYear.label'),
            subtitle: t(
                'analytics.benchmarking.comparedTo.baselineYear.subtitle'
            ),
            warningMessage: getWarningMessage(
                baselineYearTimeline,
                'analytics.benchmarking.comparedTo.baselineYear.no_baseline_year'
            ),
        },
    ]

    const getTimelineText = useCallback(
        (optionValue: ComparisonPeriod) => {
            if (optionValue === 'previousYear') {
                if (!previousYearTimeline) {
                    return null
                }
                return formatComparedToTimeline(previousYearTimeline)
            }

            if (!baselineYearTimeline) {
                return null
            }
            return formatComparedToTimeline(baselineYearTimeline)
        },
        [baselineYearTimeline, previousYearTimeline]
    )

    return (
        <RadioOptionPicker
            name="compared-to"
            label={t('analytics.benchmarking.comparedTo.label')}
            value={value}
            options={options}
            onChange={(value: string) => {
                onChange(value as ComparisonPeriod)
            }}
            renderLabel={(option) => {
                const optionTimelineText = getTimelineText(
                    option.value as ComparisonPeriod
                )

                return (
                    <div className="flex flex-col">
                        <div className="text-label-small text-regular">
                            {option.label}
                        </div>
                        {optionTimelineText && (
                            <div className="mb-1 flex items-center gap-2">
                                <span className="flex h-6 items-center justify-center rounded-full border border-blue-100 bg-blue-50 px-2 py-2">
                                    {optionTimelineText}
                                </span>
                            </div>
                        )}
                        <div className="text-body-small text-subtle">
                            {option.subtitle}
                        </div>
                        {option.warningMessage && (
                            <div className="text-body-small mt-2 text-subtle">
                                {option.warningMessage}
                            </div>
                        )}
                    </div>
                )
            }}
        />
    )
}
